import { WordRunFn } from "../global";

/**
 * Represents the current state of the modification handler.
 * - `unregistered`: Initial state, event handlers not yet registered
 * - `idle`: Active and listening for document modifications
 * - `paused`: Temporarily ignoring modification events
 * - `resuming`: Transitioning back to idle, waiting for timeout or next event
 */
export type ModificationPhase = "unregistered" | "idle" | "paused" | "resuming";

/**
 * Manages Office document modification events with a state machine pattern.
 * 
 * Provides pause/resume capabilities to temporarily suppress event handling
 * during programmatic document changes, preventing recursive or unwanted
 * modification notifications.
 * 
 * @example
 * ```typescript
 * const handler = new ModificationHandler();
 * await handler.register(Word.run);
 * 
 * handler.onModification(() => console.log("Document changed"));
 * handler.onPhaseChange((phase, prev) => console.log(`${prev} -> ${phase}`));
 * 
 * handler.pause();
 * // Make programmatic changes without triggering events
 * await handler.resume(1000);
 * ```
 */
export class ModificationHandler {
  /** Current phase of the modification handler state machine */
  private _phase: ModificationPhase = "unregistered";
  private _phaseListeners: Set<(phase: ModificationPhase, prev: ModificationPhase) => void> = new Set();
  private _modificationListeners: Set<() => void> = new Set();
  private _resumeResolve: (() => void) | null = null;
  private _resumeTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor() { }

  /** Returns the current phase of the handler */
  get phase(): ModificationPhase {
    return this._phase;
  }

  /**
   * Registers Office event handlers for paragraph modifications.
   * Transitions the handler from "unregistered" to "idle" phase.
   * Must be called before the handler can receive modification events.
   * 
   * @param run - Word.run function from Office.js
   */
  async register(run: WordRunFn) {
    return run(async (context) => {
      console.log("[modifications] Registering Office event handlers");

      this.transition("idle");

      const handleModification = () => {
        if (this._phase === "paused") {
          // Ignore events while paused
          return;
        }

        if (this._phase === "resuming") {
          // Clear timeout and resolve immediately
          if (this._resumeTimeoutId) {
            clearTimeout(this._resumeTimeoutId);
            this._resumeTimeoutId = null;
          }
          if (this._resumeResolve) {
            const resolve = this._resumeResolve;
            this._resumeResolve = null;
            this.transition("idle");
            resolve();
          }
          return;
        }

        this.notifyModificationListeners();
      };

      context.document.onParagraphDeleted.add(() => {
        console.log("[modifications] onParagraphDeleted");
        handleModification();
        return Promise.resolve()
      });

      context.document.onParagraphAdded.add(() => {
        console.log("[modifications] onParagraphAdded");
        handleModification();
        return Promise.resolve()
      });

      context.document.onParagraphChanged.add(() => {
        console.log("[modifications] onParagraphChanged");
        handleModification();
        return Promise.resolve()
      });

      await context.sync();
      console.log("[modifications-dev] Handlers registered successfully");
    });
  }

  /**
   * Subscribes to phase change events.
   * 
   * @param cb - Callback invoked with new phase and previous phase
   * @returns Unsubscribe function
   */
  onPhaseChange(cb: (phase: ModificationPhase, prev: ModificationPhase) => void): () => void {
    this._phaseListeners.add(cb);
    return () => {
      this._phaseListeners.delete(cb);
    };
  }

  /**
   * Subscribes to document modification events.
   * Events are not triggered while the handler is paused.
   * 
   * @param cb - Callback invoked when a document modification occurs
   * @returns Unsubscribe function
   */
  onModification(cb: () => void): () => void {
    this._modificationListeners.add(cb);
    return () => {
      this._modificationListeners.delete(cb);
    };
  }

  /**
   * Pauses modification event handling.
   * Use before making programmatic changes to prevent unwanted event notifications.
   * 
   * @throws Error if not in "idle" phase
   */
  pause(): void {
    if (this._phase !== "idle") {
      throw new Error(`Cannot pause from phase: ${this._phase}`);
    }
    this.transition("paused");
  }

  /**
   * Resumes modification event handling after a pause.
   * Transitions to "resuming" phase, then resolves when either:
   * - A modification event occurs (immediate resume)
   * - The timeout elapses (forced resume)
   * 
   * @param timeoutMs - Maximum time to wait for a modification event
   * @returns Promise that resolves when resumed to "idle" phase
   * @throws Error if not in "paused" phase
   */
  resume(timeoutMs: number): Promise<void> {
    if (this._phase !== "paused") {
      return Promise.reject(new Error(`Cannot resume from phase: ${this._phase}`));
    }

    this.transition("resuming");

    return new Promise((resolve) => {
      this._resumeResolve = resolve;
      this._resumeTimeoutId = setTimeout(() => {
        if (this._resumeResolve) {
          this._resumeResolve = null;
          this._resumeTimeoutId = null;
          this.transition("idle");
          resolve();
        }
      }, timeoutMs);
    });
  }

  /** Transitions to a new phase and notifies all phase listeners */
  private transition(phase: ModificationPhase): void {
    const prev = this._phase;
    this._phase = phase;

    for (const listener of this._phaseListeners) {
      listener(phase, prev);
    }
  }

  /** Notifies all modification listeners of a document change */
  private notifyModificationListeners(): void {
    for (const listener of this._modificationListeners) {
      listener();
    }
  }
}
